/**
 * 0418 指导销售操作：拆分测算（门禁）→ 计算 → 批量挂卖。
 * 拆分页与仪表盘共用，避免两处参数/接口漂移。
 */
import { useMemo, useState, type CSSProperties } from 'react';
import { Alert, Button, Card, Input, Space, Table, Tabs, Tag, Typography, message } from 'antd';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../api/client';
import { useAuthStore } from '../store/auth';

export type GuideSalesCalc = {
  source?: string;
  unit_price?: string;
  sum_uo?: string | number;
  ap?: string | number;
  bj?: string | number;
  ymc?: string | number;
  interval1?: string;
  interval2?: string;
  interval_people1?: string | number;
  interval_people2?: string | number;
  net_weight_interval1?: string;
  net_weight_interval2?: string;
  net_weight_people1?: string | number;
  net_weight_people2?: string | number;
  trigger_count?: number;
  trigger_sum_uo?: string;
  trigger_sum_ap?: string;
  preview_rows?: GuideSalesPreviewRow[];
};

export type GuideSalesPreviewRow = {
  player_number: string;
  account: string;
  user_id: number;
  tier: '100' | '1000';
  wallet_uo: string;
  hang_uo: string;
  remain_uo: string;
  rem: string;
  will_guide: boolean;
  is_exit: boolean;
  has_open_sell: boolean;
};

export type SplitCycleState = {
  phase: string;
  open_guide_sell_uo: string;
  can_execute_split: boolean;
  can_trigger_guide_sales: boolean;
  pending_ratio: string | null;
  guide_sales_calc?: GuideSalesCalc;
};

function Field({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div style={{ flex: 1, minWidth: 140 }}>
      <div style={{ marginBottom: 4, fontSize: 12 }}>{label}</div>
      <Input value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function num(v: string | number | undefined): number {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
}

function avg(total: number, count: number): string {
  if (count <= 0) return '—';
  return (total / count).toFixed(1);
}

function CalcSummary({
  g,
  level1Usd,
  level2Usd,
  coins,
}: {
  g: GuideSalesCalc;
  level1Usd: string;
  level2Usd: string;
  coins: string;
}) {
  const face = num(g.unit_price) || 0.2;
  const retain1Orb = face > 0 ? (num(level1Usd) / face).toFixed(0) : '—';
  const retain2Orb = face > 0 ? (num(level2Usd) / face).toFixed(0) : '—';
  const walletUsd3000 = 3000 * face;
  const hangUsd3000 = Math.max(walletUsd3000 - num(level2Usd), 0);
  const hangOrb3000 = face > 0 ? Math.floor(hangUsd3000 / face) : 0;
  const people1 = num(g.interval_people1);
  const people2 = num(g.interval_people2);
  const peopleAll = people1 + people2;
  const sumOrb = num(g.sum_uo);
  const hangN = num(g.trigger_count);
  const hangOrb = num(g.trigger_sum_uo);

  const row: CSSProperties = { display: 'flex', gap: 16, flexWrap: 'wrap' };
  const box: CSSProperties = {
    flex: '1 1 220px',
    minWidth: 200,
    padding: '8px 10px',
    background: '#fafafa',
    border: '1px solid #f0f0f0',
    borderRadius: 4,
    lineHeight: 1.7,
  };

  return (
    <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.75)', marginBottom: 12 }}>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>测算清单</div>
      <div style={row}>
        <div style={box}>
          <div>来源 {g.source === 'php_live' ? '线网 PHP' : '本地镜像'} · 当前价 {g.unit_price}</div>
          <div>过线才可以挂卖</div>
          <div>最终价 {g.bj}（只计满档，不含超卖）</div>
          <div>
            超卖 {g.ymc}
            {num(coins) > 0
              ? `（未满档 ${((num(g.ymc) / num(coins)) * 100).toFixed(1)}%，越大越不准）`
              : ''}
          </div>
        </div>
        <div style={box}>
          <div>过线 {peopleAll} 人 · {sumOrb.toLocaleString()} ORB</div>
          <div>100¥ 档 {people1} 人（{g.interval1 || '—'}）</div>
          <div>$1000 档 {people2} 人（{g.interval2 || '—'}）</div>
        </div>
        <div style={box}>
          <div>出局 100¥ {g.net_weight_people1} 人（{g.net_weight_interval1 || '—'}）</div>
          <div>出局 $1000 档 {g.net_weight_people2} 人（{g.net_weight_interval2 || '—'}）</div>
        </div>
        <div style={box}>
          <div>
            $1000档挂卖=($钱包−$保留)÷当前价 · 典型3000×{g.unit_price}={walletUsd3000.toFixed(0)}，留 {level2Usd}，挂{' '}
            {hangUsd3000} 美金={hangOrb3000} ORB
          </div>
          <div>$100档 300 ORB×当前价−留 {level1Usd} 美金 → 挂 {Math.max(300 - num(retain1Orb), 0)} ORB</div>
          <div>过线可挂卖 {hangN.toLocaleString()} 户 · {hangOrb.toLocaleString()} ORB</div>
          <div>挂卖每户 {avg(hangOrb, hangN)} ORB（已有挂卖的执行时跳过）</div>
        </div>
      </div>
    </div>
  );
}

function rowStatus(r: GuideSalesPreviewRow): { label: string; color: string } {
  if (r.will_guide && r.is_exit) return { label: '指导销售 · 出局', color: 'orange' };
  if (r.will_guide) return { label: '指导销售', color: 'blue' };
  if (r.is_exit) return { label: '出局', color: 'red' };
  if (r.has_open_sell) return { label: '已有挂卖', color: 'default' };
  return { label: '过线不挂', color: 'default' };
}

function PreviewAccountTable({ rows }: { rows: GuideSalesPreviewRow[] }) {
  const [tab, setTab] = useState('guide');
  const [q, setQ] = useState('');

  const filtered = useMemo(() => {
    const term = q.trim();
    return rows.filter((r) => {
      if (tab === 'guide' && !r.will_guide) return false;
      if (tab === 'exit' && !r.is_exit) return false;
      if (tab === 'skip' && (r.will_guide || r.is_exit)) return false;
      if (!term) return true;
      return r.player_number.includes(term) || r.account.toLowerCase().includes(term.toLowerCase());
    });
  }, [rows, tab, q]);

  const guideN = rows.filter((r) => r.will_guide).length;
  const exitN = rows.filter((r) => r.is_exit).length;

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ fontWeight: 600, marginBottom: 8 }}>账户预览</div>
      <Tabs
        size="small"
        activeKey={tab}
        onChange={setTab}
        items={[
          { key: 'guide', label: `将指导销售 ${guideN}` },
          { key: 'exit', label: `出局 ${exitN}` },
          { key: 'all', label: `全部过线 ${rows.length}` },
          { key: 'skip', label: '不挂卖' },
        ]}
        tabBarExtraContent={
          <Input
            allowClear
            size="small"
            placeholder="搜索编号 / 账户"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            style={{ width: 200 }}
          />
        }
      />
      <Table
        size="small"
        rowKey={(r) => `${r.player_number}-${r.user_id}`}
        dataSource={filtered}
        pagination={{ pageSize: 50, showSizeChanger: true, showTotal: (n) => `共 ${n} 户` }}
        scroll={{ x: 880 }}
        columns={[
          { title: '编号', dataIndex: 'player_number', width: 100 },
          { title: '账户', dataIndex: 'account', width: 140, ellipsis: true },
          {
            title: '档位',
            dataIndex: 'tier',
            width: 80,
            render: (t: string) => (t === '1000' ? '$1000' : '$100'),
          },
          { title: '钱包 ORB', dataIndex: 'wallet_uo', width: 100, align: 'right' },
          {
            title: 'ORB变化',
            dataIndex: 'hang_uo',
            width: 100,
            align: 'right',
            render: (v: string, r) => (r.will_guide ? `-${v}` : '0'),
          },
          { title: '挂后剩余', dataIndex: 'remain_uo', width: 100, align: 'right' },
          { title: '剩余净量', dataIndex: 'rem', width: 90, align: 'right' },
          {
            title: '状态',
            width: 140,
            render: (_, r) => {
              const s = rowStatus(r);
              return <Tag color={s.color}>{s.label}</Tag>;
            },
          },
        ]}
      />
    </div>
  );
}

export function GuideSalesOpsCard({
  variant = 'embedded',
  includeSplitPreview = false,
}: {
  variant?: 'card' | 'embedded';
  /** 仪表盘需要自带测算，否则「指导销售」会一直灰 */
  includeSplitPreview?: boolean;
}) {
  const qc = useQueryClient();
  const hasNode = useAuthStore((s) => s.hasNode);
  const hasMenu = useAuthStore((s) => s.hasMenu);
  const isSuper = useAuthStore((s) => s.profile?.is_super === true);
  const canList =
    isSuper ||
    hasNode('platform.split.list') ||
    hasMenu('parity.uo_config') ||
    hasNode('parity.uo_config') ||
    hasNode('platform.dashboard') ||
    hasNode('platform.dashboard.view');
  const canTrigger =
    isSuper || hasNode('platform.split.trigger') || hasMenu('parity.uo_config') || hasNode('parity.uo_config');

  const [triggerRatio, setTriggerRatio] = useState('0.1');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [level1Retain, setLevel1Retain] = useState('50');
  const [level2Retain, setLevel2Retain] = useState('500');
  const [netWeight1, setNetWeight1] = useState('10');
  const [netWeight2, setNetWeight2] = useState('100');
  const [guideCoins, setGuideCoins] = useState('1000000');
  const [guideSalesLoading, setGuideSalesLoading] = useState(false);
  const [guidePreview, setGuidePreview] = useState<GuideSalesCalc | null>(null);

  const { data: cycleState, refetch: refetchCycle } = useQuery({
    queryKey: ['admin', 'split', 'cycle-state'],
    queryFn: () => api.get<SplitCycleState>('/api/admin/split-log/cycle/state'),
    enabled: canList,
    refetchInterval: 30_000,
  });

  async function runSplitPreview() {
    setPreviewLoading(true);
    try {
      await api.post('/api/admin/split-log/cycle/split-preview', {
        pool: 'uo',
        ratio: triggerRatio,
      });
      void refetchCycle();
      message.success('拆分测算已保存，可计算并触发指导销售');
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : '拆分测算失败');
    } finally {
      setPreviewLoading(false);
    }
  }

  async function runGuideSalesPreview() {
    setGuideSalesLoading(true);
    try {
      const res = (await api.post(
        '/api/admin/split-log/cycle/guide-sales-preview',
        {
          level1: level1Retain,
          level2: level2Retain,
          net_weight1: netWeight1,
          net_weight2: netWeight2,
          coins: guideCoins,
        },
        { timeout: 120_000 },
      )) as GuideSalesCalc;
      setGuidePreview(res);
      void refetchCycle();
      message.success(
        `计算完成（${res?.source === 'php_live' ? '线网' : '镜像'}）：过线 ${res?.interval_people1}/${res?.interval_people2} · 出局 ${res?.net_weight_people1}/${res?.net_weight_people2} · 待挂卖 ${res?.trigger_count ?? 0} 户`,
      );
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : '指导销售测算失败');
    } finally {
      setGuideSalesLoading(false);
    }
  }

  async function triggerGuideSales() {
    setGuideSalesLoading(true);
    try {
      const res = (await api.post('/api/admin/split-log/cycle/guide-sales-trigger', {
        level1: level1Retain,
        level2: level2Retain,
        net_weight1: netWeight1,
        net_weight2: netWeight2,
        coins: guideCoins,
        dry_run: false,
      })) as { listed_count: number; skipped_count: number };
      message.success(`指导销售已挂卖 ${res.listed_count} 单（跳过 ${res.skipped_count}）`);
      void refetchCycle();
      void qc.invalidateQueries({ queryKey: ['admin', 'dashboard'] });
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : '指导销售触发失败');
    } finally {
      setGuideSalesLoading(false);
    }
  }

  const calc = guidePreview ?? cycleState?.guide_sales_calc;
  const phaseAlert = cycleState && (
    <Alert
      type={
        cycleState.phase === 'ready_to_split'
          ? 'success'
          : cycleState.phase === 'guide_sales_active'
            ? 'warning'
            : 'info'
      }
      showIcon
      style={{ marginBottom: 12 }}
      message={`周期阶段：${cycleState.phase} · 未成交指导销售 ORB：${cycleState.open_guide_sell_uo}`}
      description="流程：拆分测算 → 指导销售批量挂卖 → 挂卖清零 → 拆分执行。静态奖在指导销售成交后发放。"
    />
  );

  const body = (
    <>
      {phaseAlert}
      {includeSplitPreview && canTrigger && (
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 12 }}>
          <div style={{ minWidth: 160 }}>
            <div style={{ marginBottom: 4, fontSize: 12 }}>拆分倍数（须先测算）</div>
            <Input
              value={triggerRatio}
              onChange={(e) => setTriggerRatio(e.target.value)}
              placeholder="例如 1.2"
            />
          </div>
          <Button
            type="primary"
            loading={previewLoading}
            disabled={!triggerRatio || Number.isNaN(parseFloat(triggerRatio))}
            onClick={() => void runSplitPreview()}
          >
            拆分测算
          </Button>
        </div>
      )}
      <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
        <Field label="$100 档挂卖保留（美金÷0.2=ORB）" value={level1Retain} onChange={setLevel1Retain} />
        <Field label="$1000挂卖保留" value={level2Retain} onChange={setLevel2Retain} />
        <Field label="$100 剩余净量低于出局" value={netWeight1} onChange={setNetWeight1} />
        <Field label="$1000 剩余净量低于出局" value={netWeight2} onChange={setNetWeight2} />
        <Field label="卖出币多少涨价（越小超卖占比越大、价越不准）" value={guideCoins} onChange={setGuideCoins} />
      </div>
      {calc && (
        <CalcSummary g={calc} level1Usd={level1Retain} level2Usd={level2Retain} coins={guideCoins} />
      )}
      {!canTrigger && (
        <Alert type="info" showIcon style={{ marginBottom: 12 }} message="无「拆分触发」权限，仅可查看周期状态" />
      )}
      <div style={{ display: 'flex', gap: 10, justifyContent: variant === 'card' ? 'flex-start' : 'center' }}>
        <Button loading={guideSalesLoading} disabled={!canTrigger} onClick={() => void runGuideSalesPreview()}>
          计算
        </Button>
        <Button
          type="primary"
          loading={guideSalesLoading}
          disabled={!canTrigger || !cycleState?.can_trigger_guide_sales}
          onClick={() => void triggerGuideSales()}
        >
          指导销售
        </Button>
      </div>
      {guidePreview?.preview_rows?.length ? (
        <PreviewAccountTable rows={guidePreview.preview_rows} />
      ) : guidePreview ? (
        <div style={{ marginTop: 12, fontSize: 12, color: 'rgba(0,0,0,0.45)' }}>本次计算没有过线账户</div>
      ) : null}
    </>
  );

  if (variant === 'embedded') {
    return (
      <div>
        <div style={{ fontWeight: 600, marginBottom: 12 }}>0418版指导销售</div>
        {body}
      </div>
    );
  }

  return (
    <Card
      size="small"
      title="0418版指导销售"
      extra={
        <Space size="small">
          <Typography.Link>
            <Link to="/platform/split/log">完整拆分页</Link>
          </Typography.Link>
        </Space>
      }
      style={{ marginBottom: 16 }}
    >
      {body}
    </Card>
  );
}
